import React, { useState, useEffect, useRef, useCallback } from 'react'
import { AudioCaptureContext } from './audio-capture-context'
import { useStoreActions, useStoreState } from 'easy-peasy'
import { useMutation, useQueryClient } from 'react-query'
import { endpoints } from '@api'
import { useExperienceManager } from '@hooks'
import { datadogLogs } from '@datadog/browser-logs'

interface ManualAudioCaptureProviderProps {
  sessionId: string
  refetchRecap: () => void
  children: React.ReactNode
}

interface UploadPayload {
  timestamp: number
  chunks: BlobPart[]
}

const ManualAudioCaptureProvider: React.FC<ManualAudioCaptureProviderProps> = ({
  sessionId,
  refetchRecap,
  children
}) => {
  const [isRecording, setIsRecording] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState('')
  const [streams, setStreams] = useState<MediaStream[]>([])
  const [permissionStatus, setPermissionStatus] = useState('unknown')
  const [hasMicAccess, setHasMicAccess] = useState(true)

  const [uploadQueue, setUploadQueue] = useState<UploadPayload[]>([])
  const [isLastChunkSet, setIsLastChunkSet] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isUploadComplete, setIsUploadComplete] = useState(false)

  const [isTesting, setIsTesting] = useState<boolean>(false)
  const [disableEchoCancelation, setDisableEchoCancelation] = useState(false)
  const [hasDetectedAudio, setHasDetectedAudio] = useState<boolean>(false)
  const [audioLastDetectedAt, setAudioLastDetectedAt] = useState<number | null>(
    null
  )
  const [showMicWarning, setShowMicWarning] = useState<boolean>(false)

  const [isTelehealth, setIsTelehealth] = useState(false)
  const [isUsingHeadphones, setIsUsingHeadphones] = useState(false)

  const audioStreamRef = useRef<MediaStream | null>(null)
  const streamsRef = useRef<MediaStream[]>([])
  const tabStreamRef = useRef<MediaStream | null | undefined>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const isRecordingRef = useRef<boolean>(isRecording)
  const isLastChunkSetRef = useRef<boolean>(isLastChunkSet)
  const isUploadCompleteRef = useRef<boolean>(isUploadComplete)
  const isTestingRef = useRef<boolean>(isTesting)
  const audioContextRef = useRef<AudioContext | null>(null)

  const volumeActions: any = useStoreActions(actions => actions.volume)

  const queryClient = useQueryClient()

  const { user } = useStoreState(state => state.auth)

  const {
    documentationAutomationFreeTierSessionLimitReached,
    isAdmin
  } = useExperienceManager()

  const {
    mutateAsync: updateSession,
    isLoading: isUpdateSessionLoading
  } = useMutation(endpoints.startAudioCapture.request, {
    onSuccess: (data: any) => {
      refetchRecap()
    }
  })

  const { mutateAsync: getPresignedUrl } = useMutation(
    endpoints.getPresignedUrl.request
  )

  const { mutateAsync: completeScribeOnboarding } = useMutation(
    endpoints.postUserAccount.request,
    {
      onSuccess() {
        queryClient.invalidateQueries(endpoints.getUserAccount.getCacheId())
      }
    }
  )

  const handleMarkOnboardingComplete = async () => {
    if (!user?.has_onboarded_to_scribe) {
      await completeScribeOnboarding({
        data: { has_onboarded_to_scribe: true }
      })
    }
  }

  const logData = {
    sessionId,
    isRecording,
    isTesting,
    hasMicAccess,
    hasDetectedAudio,
    audioLastDetectedAt,
    showMicWarning,
    devices,
    selectedAudioInput: selectedDeviceId,

    selectedAudioInputLabel: devices.find(i => i.deviceId === selectedDeviceId)
      ?.label,
    documentationAutomationFreeTierSessionLimitReached,
    isAdmin
  }

  // keep refs in sync w/ state
  useEffect(() => {
    streamsRef.current = streams
  }, [streams])

  useEffect(() => {
    isRecordingRef.current = isRecording
  }, [isRecording])

  useEffect(() => {
    isTestingRef.current = isTesting
  }, [isTesting])

  useEffect(() => {
    isUploadCompleteRef.current = isUploadComplete
  }, [isUploadComplete])

  useEffect(() => {
    isLastChunkSetRef.current = isLastChunkSet
  }, [isLastChunkSet])

  // clean up audio streams on unmount
  useEffect(() => {
    return () => {
      stopAudioStream()
      stopRecording()
    }
  }, [])

  useEffect(() => {
    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then(async permissionStatus => {
        if (permissionStatus.state === 'denied') {
          setPermissionStatus('denied')
          setHasMicAccess(false)
        } else if (permissionStatus.state === 'granted') {
          setPermissionStatus('granted')
          await getInputDevices({ setDefault: true })
        } else {
          setPermissionStatus('unknown')
        }
      })
      .catch(error => {
        console.error('Error querying microphone permission:', error)
      })
  }, [])

  useEffect(() => {
    if (uploadQueue.length > 0 && !isUploading) {
      const payload = uploadQueue.shift()
      if (payload) {
        uploadChunk(payload)
      }
    }
  }, [uploadQueue, isUploading])

  useEffect(() => {
    const refresh = async () => {
      await refreshMediaRecorder()
    }

    if (selectedDeviceId && isRecording) {
      refresh()
    }
  }, [selectedDeviceId, isRecording, disableEchoCancelation])

  useEffect(() => {
    if (selectedDeviceId && isTesting) {
      createAudioStream()
    }
  }, [selectedDeviceId, isTesting])

  useEffect(() => {
    if (audioStreamRef.current) {
      audioStreamRef.current
        .getAudioTracks()
        .forEach(t => (t.enabled = !isMuted))
    }
  }, [isMuted])

  useEffect(() => {
    // after starting recording, check for audio after 15 seconds
    const interval = setInterval(() => {
      if (isRecording && !audioLastDetectedAt && !isMuted) {
        setShowMicWarning(true)
      }
    }, 15000)

    return () => clearInterval(interval)
  }, [audioLastDetectedAt, isRecording])

  useEffect(() => {
    // after the first audio check, continue to check every 60 seconds
    const interval = setInterval(() => {
      if (
        isRecording &&
        !isMuted &&
        audioLastDetectedAt &&
        Date.now() - audioLastDetectedAt > 60
      ) {
        setShowMicWarning(true)
      }
    }, 60000)

    return () => clearInterval(interval)
  }, [audioLastDetectedAt, isRecording, isMuted])

  const getInputDevices = async ({
    setDefault = true
  }: {
    setDefault: boolean
  }) => {
    datadogLogs.logger.info('[Audio Capture] Getting input devices', logData)
    const devices = await navigator.mediaDevices.enumerateDevices()
    const microphoneDevices = devices.filter(
      device => device.kind === 'audioinput' && !!device.deviceId
    )
    if (microphoneDevices.length) {
      setDevices(microphoneDevices)
      if (setDefault) {
        setSelectedDeviceId(microphoneDevices[0].deviceId)
      }
    }
  }

  const handlePromptForDevicePermissions = async () => {
    try {
      datadogLogs.logger.info('[Audio Capture] Prompting for permissions')
      setPermissionStatus('requested')
      await createAudioStream()
      await getInputDevices({ setDefault: true })
      setPermissionStatus('granted')
      setIsTesting(true)
    } catch (err) {
      datadogLogs.logger.info(
        '[Audio Capture] Prompting for permissions error',
        { err, ...logData }
      )
      setPermissionStatus('denied')
      setHasMicAccess(false)
    }
  }

  const testAudioInputs = async () => {
    if (permissionStatus === 'granted') {
      setIsTesting(true)
    }
  }

  const createAudioStream = async () => {
    let constraints: MediaStreamConstraints = {
      audio: {
        echoCancellation: !disableEchoCancelation,
        autoGainControl: !disableEchoCancelation
      }
    }

    if (selectedDeviceId) {
      constraints = {
        audio: {
          deviceId: { exact: selectedDeviceId },
          echoCancellation: !disableEchoCancelation,
          autoGainControl: !disableEchoCancelation
        }
      }
    }

    datadogLogs.logger.info('[Audio Capture] Creating audio stream', {
      constraints,
      ...logData
    })

    const stream = await navigator.mediaDevices.getUserMedia(constraints)
    setStreams(prevState => [...prevState, stream])

    const audioContext = new (window.AudioContext ||
      // @ts-ignore
      window.webkitAudioContext)()
    audioContextRef.current = audioContext

    const analyzer = audioContext.createAnalyser()
    const micSource = audioContext.createMediaStreamSource(stream)
    const dest = audioContext.createMediaStreamDestination()

    const gainNode = audioContext.createGain()

    micSource.connect(gainNode)
    micSource.connect(analyzer)
    micSource.connect(dest)

    if (tabStreamRef.current) {
      datadogLogs.logger.info('[Audio Capture] Attempting to mix streams', {
        ...logData
      })
      const tabSource = audioContext.createMediaStreamSource(
        tabStreamRef.current
      )
      tabStreamRef.current.getAudioTracks().forEach(track => {
        datadogLogs.logger.info('[Audio Capture] Adding track to main stream', {
          ...logData
        })
        stream.addTrack(track)
      })
      tabSource.connect(gainNode)
      tabSource.connect(analyzer)
      tabSource.connect(dest)
    }

    const array = new Uint8Array(analyzer.fftSize)

    let lastDetected = Date.now()

    function getPeakLevel() {
      analyzer.getByteTimeDomainData(array)
      return (
        array.reduce(
          (max, current) => Math.max(max, Math.abs(current - 127)),
          0
        ) / 128
      )
    }

    function tick() {
      if (isRecordingRef.current || isTestingRef.current) {
        const peak = getPeakLevel()
        if (disableEchoCancelation) {
          volumeActions.setAudioLevel(Math.min(peak * 10, 0.99))
        } else {
          volumeActions.setAudioLevel(Math.min(peak), 0.99)
        }
        if (peak && peak > 0.01) {
          setHasDetectedAudio(true)
          if (Math.abs(Date.now() - lastDetected) >= 5000) {
            if (isRecordingRef.current) {
              setAudioLastDetectedAt(Date.now())
              setShowMicWarning(false)
            }
            lastDetected = Date.now()
          }
        }
        requestAnimationFrame(tick)
      }
    }
    tick()
    audioStreamRef.current = dest.stream
    return dest.stream
  }

  const stopMediaRecorder = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop()
    }
  }

  const stopAudioStream = () => {
    streamsRef.current.forEach(s => s.getTracks().forEach(t => t.stop()))
    volumeActions.setAudioLevel(0)
  }

  const stopAudioShare = () => {
    if (tabStreamRef.current) {
      tabStreamRef.current.getTracks().forEach(t => t.stop())
    }
  }

  const stopRecording = async () => {
    if (isRecording) {
      datadogLogs.logger.info('[Audio Capture] Stop recording', { ...logData })
      setIsRecording(false)

      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === 'recording'
      ) {
        mediaRecorderRef.current.requestData()
      }

      await new Promise(resolve => {
        const interval = setInterval(() => {
          stopMediaRecorder()
          stopAudioStream()
          stopAudioShare()
          setIsTesting(false)
          if (isUploadCompleteRef.current) {
            clearInterval(interval)
            resolve(true)
          }
        }, 100)
      })
    }
  }

  const uploadChunk = async (
    payload: UploadPayload,
    retryCount: number = 0
  ) => {
    const baseDelay = 1000

    setIsUploading(true)

    const { timestamp, chunks } = payload
    const blob = new Blob(chunks)

    try {
      const data: any = await getPresignedUrl({
        sessionId,
        timestamp
      })
      await fetch(data.url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'audio/ogg'
        },
        body: blob
      })

      if (isLastChunkSetRef.current) {
        setIsUploadComplete(true)
      }
      setIsUploading(false)
    } catch (error) {
      const delay = baseDelay * Math.min(Math.pow(2, retryCount), 20)
      datadogLogs.logger.error(
        `[Audio Capture] Upload failed. Retry attempt ${retryCount +
          1}. Retrying in ${delay} milliseconds.`,
        {
          ...logData,
          error
        }
      )
      setTimeout(() => {
        uploadChunk(payload, retryCount + 1)
      }, delay)

      setIsUploading(false)
    }
  }

  const createMediaRecorder = async (audioStream: MediaStream) => {
    datadogLogs.logger.info('[Audio Capture] Create media recorder', {
      ...logData
    })

    let audioChunks: BlobPart[] = []

    const mediaRecorder = new MediaRecorder(audioStream)
    mediaRecorderRef.current = mediaRecorder

    mediaRecorder.addEventListener('dataavailable', async function(event) {
      if (event.data.size > 0) {
        audioChunks.push(event.data)
      }
    })

    mediaRecorder.addEventListener('stop', async () => {
      const chunks = audioChunks.splice(0, audioChunks.length)
      const payload = {
        timestamp: Date.now(),
        chunks
      }
      setUploadQueue(prevQueue => [...prevQueue, payload])
      if (!isRecordingRef.current) {
        setIsLastChunkSet(true)
      }
    })
  }

  const refreshMediaRecorder = async () => {
    datadogLogs.logger.info('[Audio Capture] Refresh media recorder', {
      ...logData
    })
    stopMediaRecorder()
    stopAudioStream()
    const stream = await createAudioStream()
    await createMediaRecorder(stream) // make sure this works
    startMediaRecorder()
  }

  const handleInputChange = async (deviceId: string) => {
    datadogLogs.logger.info('[Audio Capture] Changing inputs', {
      ...logData,
      deviceId
    })
    setSelectedDeviceId(deviceId)
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close()
      } catch (err) {
        datadogLogs.logger.warn(
          '[Audio Capture] Unable to close audio context',
          logData
        )
      }
    }
    setAudioLastDetectedAt(null)
  }

  const startMediaRecorder = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.start()
      setTimeout(() => {
        if (mediaRecorderRef.current) {
          if (mediaRecorderRef.current.state === 'recording') {
            stopMediaRecorder()
          }
          if (isRecordingRef.current) {
            startMediaRecorder()
          }
        }
      }, 5000)
    }
  }

  const toggleMute = () => {
    datadogLogs.logger.info('[Audio Capture] Toggling mute', { ...logData })
    setIsMuted(!isMuted)
  }

  const startRecording = async ({
    isUsingHeadphones,
    isTelehealth
  }: {
    isUsingHeadphones: boolean
    isTelehealth: boolean
  }) => {
    if (isRecording) return

    datadogLogs.logger.info('[Audio Capture] Start recording', { ...logData })

    if (!selectedDeviceId) {
      datadogLogs.logger.info(
        '[Audio Capture] Start recording - Prompting for permissions',
        { ...logData }
      )
      // safari requires access on each page load
      await handlePromptForDevicePermissions()
    }

    await updateSession({
      sessionId,
      isTelehealth,
      isUsingHeadphones
    })

    await handleMarkOnboardingComplete()

    setIsRecording(true)
    setIsTesting(false)
  }

  const startContentShare = async () => {
    try {
      datadogLogs.logger.info('[Audio Share] Attempting audio share', logData)
      const mediaStream = await navigator.mediaDevices.getDisplayMedia({
        audio: true,
        video: true
      })
      mediaStream.getTracks().forEach(track => {
        track.onended = () => {
          refreshMediaRecorder()
        }
      })

      tabStreamRef.current = mediaStream // do not set on state to make sure we don't end it
    } catch (error) {
      datadogLogs.logger.warn('[Audio Share] Error sharing audio', {
        ...logData,
        error
      })
    }
  }

  const contextValue = {
    isReady: true,
    audioInputs: devices,
    selectedAudioInput: selectedDeviceId,
    setSelectedAudioInput: handleInputChange,
    testAudioInputs,
    hasMicAccess,
    setHasMicAccess,
    promptForDevicePermissions: handlePromptForDevicePermissions,
    isMuted,
    toggleMute,
    disableEchoCancelation: setDisableEchoCancelation,
    isRecording,
    isRecordingLoading: isUpdateSessionLoading,
    startRecording,
    stopRecording,
    startContentShare,
    stopContentShare: () => {},
    logData,
    hasDetectedAudio,
    showMicWarning,
    setShowMicWarning,
    isUsingHeadphones,
    setIsUsingHeadphones,
    isTelehealth,
    setIsTelehealth,
  }

  return (
    <AudioCaptureContext.Provider value={contextValue}>
      {children}
    </AudioCaptureContext.Provider>
  )
}

export default ManualAudioCaptureProvider
